<template>
  <div class="c-share-request-actions__container">
    <div v-if="!dataHasBeenRequested">
      <component
        :is="hasDataSharingHub ? BittsTooltip : 'div'"
        placement="topRight"
      >
        <BittsButton
          text="Request data"
          :size="hasDataSharingHub ? 'small' : 'x-small'"
          type="neutral"
          :variant="hasDataSharingHub ? 'outline' : 'fill'"
          @click="emits('data-requested')"
        />
        <template #title>
          Send your partner a detailed message describing the data or specific
          fields you’d like access to.
        </template>
      </component>
    </div>
    <div v-else>
      <div class="flex items-center">
        <BittsTag text="Pending" class="mr-4" />
        <BittsPopover placement="bottomRight">
          <template #content>
            <div class="p-4">
              <component
                :is="divOrToolip"
                data-testid="reminderOption"
                :mount-to-body="true"
                :class="{
                  'c-share-request-actions__option-disabled': !canSendReminder,
                }"
                @[handleClick]="onSendReminderClicked"
              >
                <template #title>
                  {{ reminderTooltip }}
                </template>
                <div class="c-share-request-actions__option">
                  <FontAwesomeIcon
                    :icon="['fak', 'poke']"
                    :style="{ height: '16px', width: '16px' }"
                    class="text-neutral-accent mr-8"
                  />
                  <p :class="{ 'opacity-50': !canSendReminder }">
                    Send reminder
                  </p>
                </div>
              </component>
              <div
                class="c-share-request-actions__option"
                data-testid="cancelOption"
                @click="onCancelRequestClicked"
              >
                <FontAwesomeIcon
                  :icon="['fak', 'ban']"
                  :style="{ height: '16px', width: '16px' }"
                  class="text-danger-accent mr-8"
                />
                <p class="text-danger-accent"> Cancel request </p>
              </div>
            </div>
          </template>
        </BittsPopover>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import {
  BittsButton,
  BittsPopover,
  BittsTag,
  BittsTooltip,
} from '@crossbeam/bitts';
import { EVENT_SITES } from '@crossbeam/itly';

import { DateTime } from 'luxon';
import { computed } from 'vue';

import useIteratively from '@/composables/useIteratively';
import { useDataRequestsStore, useFlashesStore } from '@/stores';
import { OutboundShareRequest } from '@/types/data_share_requests';

const {
  dataRequestInformation,
  hasDataSharingHub = false,
  eventSite = EVENT_SITES.SHARE_REQUEST_ACTION_PARTNER_DETAILS,
} = defineProps<{
  dataRequestInformation?: OutboundShareRequest | null;
  hasDataSharingHub?: boolean;
  eventSite?: string;
}>();

const emits = defineEmits(['data-requested', 'share-request-action-event']);

const dataRequestsStore = useDataRequestsStore();

const MIN_DAYS_REQUIRED = 4;
const { iteratively } = useIteratively();

const dataHasBeenRequested = computed(() => !!dataRequestInformation);

const isReminder = computed(
  () => !!dataRequestInformation?.reminded_by_user_id,
);
const eventTime = computed(() =>
  isReminder.value
    ? dataRequestInformation?.updated_at
    : dataRequestInformation?.created_at,
);

const divOrToolip = computed(() =>
  canSendReminder.value ? 'div' : BittsTooltip,
);
const reminderTooltip = computed(() =>
  isReminder.value
    ? 'Reminder has already been sent'
    : `You can send a reminder to this partner if they have not responded to your data request in ${Math.round(MIN_DAYS_REQUIRED - daysSentAgo())} days`,
);

function daysSentAgo() {
  const created = DateTime.fromISO(eventTime.value ?? '');
  const now = DateTime.now();
  return now.diff(created, 'days').toObject().days ?? 0;
}
const meetsDayRequirement = computed(() => {
  return daysSentAgo() >= MIN_DAYS_REQUIRED;
});
const canSendReminder = computed(
  () => !isReminder.value && meetsDayRequirement.value,
);

const handleClick = computed(() => (canSendReminder.value ? 'click' : null));

async function onSendReminderClicked() {
  if (!dataRequestInformation) return;

  const flashesStore = useFlashesStore();
  await dataRequestsStore.sendShareRequestReminder(dataRequestInformation.id);
  flashesStore.addSuccessFlash({
    message: 'Reminder Sent',
    description: `Your data request reminder has been sent. You’ll receive a notification if your partner
    shares fields from this population with you`,
  });
  iteratively.userRequestedDataShare({
    share_request_id: dataRequestInformation.id,
    partner_org_id: dataRequestInformation.partner_org_id.toString(),
    partner_population_id:
      dataRequestInformation.partner_population_id.toString(),
    event_site: eventSite,
    reminder_nudge: true,
  });
  await dataRequestsStore.refreshDataRequestsStore();
  const updatedShareRequest = dataRequestsStore.getOutboundShareRequestById(
    dataRequestInformation.id,
  );
  if (updatedShareRequest)
    emits('share-request-action-event', updatedShareRequest);
}

async function onCancelRequestClicked() {
  if (!dataRequestInformation) return;

  await dataRequestsStore.revokeShareRequest(dataRequestInformation.id);
  emits('share-request-action-event', undefined);
}
</script>
<style scoped lang="pcss">
@reference "../../css/theme.css";
.c-share-request-actions__container {
  @apply flex h-full items-center;
  .c-share-request-actions__label-title {
    @apply text-neutral-text-weak text-sm mb-4;
  }
  .c-share-request-actions__option {
    @apply flex items-center px-8 py-4 cursor-pointer hover:bg-neutral-background mb-4 last:mb-0 rounded-4;
  }
  .c-share-request-actions__option-disabled {
    @apply cursor-not-allowed opacity-50;
  }
}
</style>
